import { useContext, useEffect, useState } from "react";
import { Search, X, SlidersHorizontal, BarChart3, FileDown, Loader2 } from 'lucide-react';

// styles
import styles from './styles.module.css';

// context
import { Context } from "../../context/UserContext";

// components
import PrevCard from "../PrevCard";
import Modal from "../Modal";
import CardNotes from "../CardNotes";
import ConfirmDeleteModal from "../ConfirmDeleteModal";

// utils
import { generateStatisticsPdf } from "../../utils/BudgetStatisticiansPdf";

const MONTHS = [
    "Janeiro","Fevereiro","Março","Abril","Maio","Junho",
    "Julho","Agosto","Setembro","Outubro","Novembro","Dezembro"
];

const STATUS_OPTIONS = [
    { value: 'all',        label: 'Todas'       },
    { value: 'pendente',   label: 'Pendente'    },
    { value: 'enviada',    label: 'Enviada'     },
    { value: 'confirmada', label: 'Confirmada'  },
    { value: 'paga',       label: 'Paga'        },
    { value: 'cancelada',  label: 'Cancelada'   },
];

const YEAR_OPTIONS = Array.from(
    { length: 10 },
    (_, i) => new Date().getFullYear() - 5 + i
);

// Configuração visual das estatísticas — mesmo padrão de cores do sistema
const STAT_CONFIG = [
    { key: 'pendente',   label: 'Pendente',   colorClass: 'statPendente'   },
    { key: 'enviada',    label: 'Enviada',    colorClass: 'statEnviada'    },
    { key: 'confirmada', label: 'Confirmada', colorClass: 'statConfirmada' },
    { key: 'paga',       label: 'Paga',       colorClass: 'statPaga'       },
    { key: 'cancelada',  label: 'Cancelada',  colorClass: 'statCancelada'  },
];

function MyNotes() {
    const {
        getInvoicesByMonth, getInvoicesByYear, getAllInvoices, getStatistics,
        user, setUser,
        deleteInvoice,
        invoices, setInvoices,
    } = useContext(Context);

    const isPaidPlan = user?.plan === 'plus' || user?.plan === 'premium';

    // ── Modo de busca: 'simple' (mês/ano) ou 'advanced' (ano/tudo) ──
    const [searchMode, setSearchMode] = useState('simple');

    // ── Filtros de mês/ano (modo simples) ──
    const [month, setMonth] = useState(new Date().getMonth() + 1);
    const [year,  setYear]  = useState(new Date().getFullYear());

    // ── Filtro de ano (modo avançado) — 'all' ou um ano específico ──
    const [advancedYear, setAdvancedYear] = useState('all');

    const [loading, setLoading] = useState(false);

    // ── Filtros locais (aplicados sobre o resultado já buscado) ──
    const [searchClient, setSearchClient] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');

    // ── Modal de nota ──
    const [selectedInvoice, setSelectedInvoice] = useState(null);
    const [showDetails, setShowDetails]         = useState(false);

    // ── Modal de confirmação de exclusão ──
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [invoiceToDelete, setInvoiceToDelete] = useState(null);

    // ── Estatísticas ──
    const [showStats, setShowStats]       = useState(false);
    const [statsData, setStatsData]       = useState(null);
    const [statsLoading, setStatsLoading] = useState(false);
    const [pdfLoading, setPdfLoading]     = useState(false);

    const token = JSON.parse(localStorage.getItem('token')) || '';

    /* ── Busca notas — modo simples (mês/ano) ── */
    async function fetchInvoicesSimple() {
        if (!token) return;
        try {
            setLoading(true);
            const data = await getInvoicesByMonth(token, month, year);
            setInvoices(data || []);
        } catch (error) {
            console.error("Erro ao buscar notas por mês:", error);
            setInvoices([]);
        } finally {
            setLoading(false);
        }
    }

    /* ── Busca notas — modo avançado (ano específico ou tudo) ── */
    async function fetchInvoicesAdvanced() {
        if (!token) return;
        try {
            setLoading(true);
            const data = advancedYear === 'all'
                ? await getAllInvoices(token)
                : await getInvoicesByYear(token, advancedYear);

            if (data?.upgradeRequired) {
                setInvoices([]);
                setSearchMode('simple'); // volta pro modo simples se perder o plano
                return;
            }

            setInvoices(data || []);
        } catch (error) {
            console.error("Erro ao buscar notas (avancado):", error);
            setInvoices([]);
        } finally {
            setLoading(false);
        }
    }

    // Re-busca sempre que os filtros relevantes do modo atual mudarem
    useEffect(() => {
        if (searchMode === 'simple') {
            fetchInvoicesSimple();
        } else {
            fetchInvoicesAdvanced();
        }
        setSearchClient('');
        setStatusFilter('all');
        setShowStats(false);
        setStatsData(null);
        // eslint-disable-next-line
    }, [searchMode, month, year, advancedYear]);

    /* ── Filtragem local (sobre o array já retornado pelo backend) ── */
    const filtered = invoices.filter(inv => {
        const matchStatus = statusFilter === 'all' ||
            String(inv.status || '').toLowerCase() === statusFilter;

        const matchClient = searchClient.trim() === '' ||
            String(inv.recipient?.name || '').toLowerCase()
                .includes(searchClient.toLowerCase().trim());

        return matchStatus && matchClient;
    });

    /* ── Handlers gerais ── */
    function openDetails(invoice) {
        setSelectedInvoice(invoice);
        setShowDetails(true);
    }

    function handleDelete(id) {
        setInvoiceToDelete(id);
        setShowDeleteModal(true);
    }

    async function confirmDelete() {
        if (!invoiceToDelete) return;
        await deleteInvoice(invoiceToDelete, token);
        setInvoices(prev => prev.filter(inv => inv._id !== invoiceToDelete));
        setShowDeleteModal(false);
        setInvoiceToDelete(null);
    }

    function toggleSearchMode() {
        setSearchMode(prev => prev === 'simple' ? 'advanced' : 'simple');
    }

    /* ── Estatísticas ── */
    async function handleToggleStats() {
        if (showStats) {
            setShowStats(false);
            return;
        }

        setStatsLoading(true);

        // Monta o escopo com base no modo/filtros atuais
        let scope, statYear, statMonth;
        if (searchMode === 'simple') {
            scope = 'month';
            statYear = year;
            statMonth = month;
        } else {
            scope = advancedYear === 'all' ? 'all' : 'year';
            statYear = advancedYear === 'all' ? null : advancedYear;
        }

        const data = await getStatistics(token, { scope, year: statYear, month: statMonth });

        if (data?.upgradeRequired) {
            setStatsLoading(false);
            return;
        }

        setStatsData(data);
        setShowStats(true);
        setStatsLoading(false);
    }

    async function handleDownloadStatsPdf() {
        if (!statsData) return;
        setPdfLoading(true);
        try {
            await generateStatisticsPdf(statsData, user);
        } catch (err) {
            console.error('Erro ao gerar PDF de estatisticas:', err);
        } finally {
            setPdfLoading(false);
        }
    }

    const periodLabel = searchMode === 'simple'
        ? `${MONTHS[month - 1]} de ${year}`
        : (advancedYear === 'all' ? 'Todo o período' : `Ano de ${advancedYear}`);

    /* ── Render ── */
    return (
        <>
            <div className={styles.container}>

                {/* ── Cabeçalho com botão de pesquisa avançada ── */}
                <div className={styles.searchHeader}>
                    <span className={styles.searchHeaderLabel}>
                        {searchMode === 'simple' ? 'Busca por período' : 'Busca avançada'}
                    </span>

                    {isPaidPlan && (
                        <button className={styles.advancedToggleBtn} onClick={toggleSearchMode}>
                            <SlidersHorizontal size={14} />
                            {searchMode === 'simple' ? 'Pesquisa avançada' : 'Voltar ao simples'}
                        </button>
                    )}
                </div>

                {/* ── MODO SIMPLES: mês + ano ── */}
                {searchMode === 'simple' && (
                    <div className={styles.filters}>
                        <select value={month} onChange={e => setMonth(Number(e.target.value))}>
                            {MONTHS.map((m, i) => (
                                <option key={i + 1} value={i + 1}>{m}</option>
                            ))}
                        </select>

                        <select value={year} onChange={e => setYear(Number(e.target.value))}>
                            {YEAR_OPTIONS.map(y => (
                                <option key={y} value={y}>{y}</option>
                            ))}
                        </select>
                    </div>
                )}

                {/* ── MODO AVANÇADO: apenas ano, com opção "Tudo" ── */}
                {searchMode === 'advanced' && (
                    <div className={styles.filters}>
                        <select
                            value={advancedYear}
                            onChange={e => setAdvancedYear(e.target.value === 'all' ? 'all' : Number(e.target.value))}
                            className={styles.advancedSelect}
                        >
                            <option value="all">Tudo (todos os anos)</option>
                            {YEAR_OPTIONS.map(y => (
                                <option key={y} value={y}>{y}</option>
                            ))}
                        </select>
                    </div>
                )}

                {/* ── Busca por nome do cliente ── */}
                <div className={styles.searchWrap}>
                    <Search size={15} className={styles.searchIcon} />
                    <input
                        type="text"
                        className={styles.searchInput}
                        placeholder="Buscar por nome do cliente..."
                        value={searchClient}
                        onChange={e => setSearchClient(e.target.value)}
                    />
                    {searchClient && (
                        <button className={styles.clearBtn} onClick={() => setSearchClient('')}>
                            <X size={14} />
                        </button>
                    )}
                </div>

                {/* ── Chips de status com scroll horizontal ── */}
                <div className={styles.statusScroll}>
                    {STATUS_OPTIONS.map(opt => (
                        <button
                            key={opt.value}
                            className={`${styles.statusChip} ${statusFilter === opt.value ? styles.statusChipActive : ''} ${styles[`chip_${opt.value}`]}`}
                            onClick={() => setStatusFilter(opt.value)}
                        >
                            {opt.label}
                        </button>
                    ))}
                </div>

                {/* ── Botão de estatísticas (plus/premium) ── */}
                {isPaidPlan && (
                    <button
                        className={`${styles.statsToggleBtn} ${showStats ? styles.statsToggleBtnActive : ''}`}
                        onClick={handleToggleStats}
                        disabled={statsLoading}
                    >
                        {statsLoading
                            ? <Loader2 size={15} className={styles.spinner} />
                            : <BarChart3 size={15} />
                        }
                        {showStats ? 'Ocultar estatísticas' : 'Ver estatísticas'}
                    </button>
                )}

                {/* ── Card de estatísticas ── */}
                {showStats && statsData && (
                    <div className={styles.statsCard}>
                        <div className={styles.statsCardHeader}>
                            <div>
                                <p className={styles.statsCardTitle}>Estatísticas</p>
                                <p className={styles.statsCardPeriod}>{periodLabel}</p>
                            </div>
                            <button
                                className={styles.statsPdfBtn}
                                onClick={handleDownloadStatsPdf}
                                disabled={pdfLoading}
                            >
                                {pdfLoading
                                    ? <Loader2 size={14} className={styles.spinner} />
                                    : <FileDown size={14} />
                                }
                                PDF
                            </button>
                        </div>

                        <div className={styles.statsGrid}>
                            {STAT_CONFIG.map(({ key, label, colorClass }) => {
                                const data = statsData.statistics?.[key] || { count: 0, total: 0 };
                                return (
                                    <div key={key} className={`${styles.statItem} ${styles[colorClass]}`}>
                                        <span className={styles.statLabel}>{label}</span>
                                        <span className={styles.statCount}>{data.count}</span>
                                        <span className={styles.statValue}>
                                            R$ {Number(data.total).toFixed(2)}
                                        </span>
                                    </div>
                                );
                            })}
                        </div>

                        <div className={styles.statsTotalRow}>
                            <span>Total geral</span>
                            <strong>{statsData.totalCount} notas · R$ {Number(statsData.totalValue).toFixed(2)}</strong>
                        </div>
                    </div>
                )}

                {/* ── Contagem de resultados ── */}
                <p className={styles.count}>
                    {loading ? 'Buscando...' : (
                        <>
                            {filtered.length} nota{filtered.length !== 1 ? 's' : ''}
                            {' · '}{periodLabel}
                            {statusFilter !== 'all' && ` · ${STATUS_OPTIONS.find(o => o.value === statusFilter)?.label}`}
                            {searchClient.trim() && ` · "${searchClient.trim()}"`}
                        </>
                    )}
                </p>

                {/* ── Lista ── */}
                {loading ? (
                    <p className={styles.loading}>Carregando notas...</p>
                ) : filtered.length > 0 ? (
                    <div className={styles.list}>
                        {filtered.map(invoice => (
                            <PrevCard
                                key={invoice._id}
                                invoice={invoice}
                                onClick={() => openDetails(invoice)}
                                onDelete={handleDelete}
                            />
                        ))}
                    </div>
                ) : (
                    <p className={styles.empty}>
                        Nenhuma nota encontrada
                        {invoices.length > 0 ? ' com esses filtros.' : ` em ${periodLabel}.`}
                    </p>
                )}

                {/* ── Modal visualizar nota ── */}
                {showDetails && selectedInvoice && (
                    <Modal
                        createdInvoice={selectedInvoice}
                        setShowModal={setShowDetails}
                        setCreatedInvoice={setSelectedInvoice}
                        user={user}
                        setUser={setUser}
                    >
                        <CardNotes createdInvoice={selectedInvoice} />
                    </Modal>
                )}
            </div>

            {/* ── Modal confirmar exclusão ── */}
            {showDeleteModal && (
                <ConfirmDeleteModal
                    onConfirm={confirmDelete}
                    onCancel={() => setShowDeleteModal(false)}
                />
            )}
        </>
    );
}

export default MyNotes;